// Reusable mocked UI building blocks for the prototype phase
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import { ChevronRight, Search, Command, X, Star, Home, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent } from './ui/dialog';
import { PageHeader } from './PageParts';
import { mockNotifications } from '../lib/mockData';

const COLORS = ['#C52429', '#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#6B7280', '#DC2626'];

export function Breadcrumbs({ items }) {
  return (
    <nav className="flex items-center text-xs text-neutral-500 dark:text-neutral-400 gap-1 mb-3" aria-label="Breadcrumbs">
      <Link to="/dashboard" className="hover:text-brand"><Home className="h-3 w-3" /></Link>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          <ChevronRight className="h-3 w-3 opacity-60" />
          {it.to ? <Link to={it.to} className="hover:text-brand">{it.label}</Link> : <span className="text-neutral-700 dark:text-neutral-300">{it.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function MockKpi({ icon: Icon, label, value, sub, tone = 'default', testid, onClick }) {
  const map = { default: 'text-brand', warn: 'text-amber-600', critical: 'text-red-600', success: 'text-emerald-600', info: 'text-blue-600' };
  const Cmp = onClick ? 'button' : 'div';
  return (
    <Cmp data-testid={testid} onClick={onClick} className="text-left rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 w-full card-hover">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-neutral-500 font-medium">{label}</p>
          <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
        </div>
        {Icon && <div className={`h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center ${map[tone]}`}><Icon className="h-5 w-5" /></div>}
      </div>
      {sub && <p className="text-xs text-neutral-500">{sub}</p>}
    </Cmp>
  );
}

export function MockBar({ data, dataKey = 'value', height = 260, color = '#C52429', tickFormatter }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
          <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={tickFormatter} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MockLine({ data, dataKey = 'value', height = 260, color = '#3B82F6' }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
          <YAxis tick={{ fontSize: 11 }} stroke="#999" />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MockArea({ data, dataKey = 'value', height = 260, color = '#10B981' }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={data}>
          <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.4} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
          <YAxis tick={{ fontSize: 11 }} stroke="#999" />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fillOpacity={1} fill="url(#ga)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MockPie({ data, height = 260 }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 11 }}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ChartCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function StatusBadge({ value, tone }) {
  const tones = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    neutral: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone] || tones.neutral}`}>{value}</span>;
}

export function MockToolbar({ searchPlaceholder = 'Buscar...', filters = [], actionLabel, onAction, extra }) {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
      <div className="flex flex-col md:flex-row gap-3 items-stretch">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input placeholder={searchPlaceholder} className="pl-9" />
        </div>
        {filters}
        {extra}
        {actionLabel && <Button onClick={onAction} className="bg-brand hover:bg-[#A81E23] text-white">{actionLabel}</Button>}
      </div>
    </div>
  );
}

export function MockTable({ columns, rows, statusMap, empty = 'Nenhum registro', renderCell, testid }) {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              {columns.map((c, i) => <th key={i} className={`px-4 py-3 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody data-testid={testid} className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {rows.length === 0 ? <tr><td colSpan={columns.length} className="p-8 text-center text-sm text-neutral-500">{empty}</td></tr> :
              rows.map((row, ri) => (
                <tr key={ri} className="tr-hover">
                  {columns.map((c, ci) => (
                    <td key={ci} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''} ${c.mono ? 'font-mono text-xs' : ''}`}>
                      {renderCell ? renderCell(row, c) : (c.status && statusMap ? <StatusBadge value={row[c.key]} tone={statusMap[row[c.key]]} /> : (row[c.key] ?? '-'))}
                    </td>
                  ))}
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      {rows.length > 0 && <div className="border-t border-neutral-100 dark:border-neutral-800 px-4 py-3 flex items-center justify-between text-xs text-neutral-500">
        <span>Mostrando {rows.length} registros (mockados)</span>
        <div className="flex gap-1">
          <button className="px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700">‹</button>
          <button className="px-2 py-1 rounded bg-brand text-white">1</button>
          <button className="px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700">2</button>
          <button className="px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700">3</button>
          <button className="px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700">›</button>
        </div>
      </div>}
    </div>
  );
}

export function EmptyStateBox({ icon: Icon, title, description, actionLabel }) {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 p-10 text-center">
      <div className="h-14 w-14 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-3">
        <Icon className="h-7 w-7 text-neutral-400" />
      </div>
      <h3 className="font-semibold">{title}</h3>
      {description && <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">{description}</p>}
      {actionLabel && <Button className="mt-4 bg-brand hover:bg-[#A81E23] text-white">{actionLabel}</Button>}
    </div>
  );
}

export function TimelineList({ events, colorFor = () => 'bg-brand' }) {
  return (
    <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-4">
      {events.map((e, i) => (
        <li key={i} className="pl-6 relative">
          <span className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900 ${colorFor(e)}`} />
          <div className="text-sm font-medium">{e.title}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{e.date} • {e.subtitle}</div>
        </li>
      ))}
    </ol>
  );
}

export function TabsShell({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800 mb-4 overflow-x-auto">
      {tabs.map(t => (
        <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => onChange(t.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${active === t.key ? 'border-brand text-brand' : 'border-transparent text-neutral-600 hover:text-brand'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function MockDrawer({ open, onClose, title, children }) {
  return (
    <div className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}>
      <div className={`absolute inset-0 bg-black/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`absolute right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-neutral-900 shadow-2xl transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-5 border-b border-neutral-200 dark:border-neutral-800 flex justify-between items-center">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto h-full pb-16">{children}</div>
      </div>
    </div>
  );
}

// Command palette (Ctrl+K)
const COMMANDS = [
  { section: 'Frota', items: [
    { label: 'Ver frota', to: '/frota' }, { label: 'Dashboard da frota', to: '/frota/dashboard' },
  ] },
  { section: 'Manutenção', items: [
    { label: 'Dashboard de manutenção', to: '/manutencao' }, { label: 'Ordens de Serviço', to: '/manutencao/os' },
    { label: 'Preventivas', to: '/manutencao/preventivas' }, { label: 'Pneus', to: '/manutencao/pneus' },
    { label: 'Abastecimentos', to: '/manutencao/abastecimentos' }, { label: 'Checklists', to: '/manutencao/checklists' },
  ] },
  { section: 'Estoque', items: [
    { label: 'Dashboard de estoque', to: '/estoque' }, { label: 'Produtos', to: '/estoque/produtos' },
    { label: 'Pedidos de compra', to: '/estoque/pedidos' }, { label: 'Fornecedores', to: '/estoque/fornecedores' },
  ] },
  { section: 'Financeiro', items: [
    { label: 'Dashboard financeiro', to: '/financeiro' }, { label: 'Contas a Receber', to: '/financeiro/receber' },
    { label: 'Contas a Pagar', to: '/financeiro/pagar' }, { label: 'Fluxo de Caixa', to: '/financeiro/fluxo-caixa' },
    { label: 'DRE', to: '/financeiro/dre' },
  ] },
  { section: 'RH', items: [
    { label: 'Dashboard RH', to: '/rh' }, { label: 'Funcionários', to: '/rh/funcionarios' },
    { label: 'Motoristas', to: '/rh/motoristas' }, { label: 'CNH', to: '/rh/cnh' },
  ] },
  { section: 'Operação', items: [
    { label: 'Dashboard operacional', to: '/operacao' }, { label: 'Linhas', to: '/operacao/linhas' },
    { label: 'Rotas', to: '/operacao/rotas' }, { label: 'Viagens', to: '/operacao/viagens' },
    { label: 'Escalas', to: '/operacao/escalas' },
  ] },
  { section: 'BI', items: [
    { label: 'Dashboard executivo', to: '/bi' }, { label: 'KPIs', to: '/bi/kpis' },
    { label: 'Comparativos', to: '/bi/comparativos' },
  ] },
  { section: 'Portais', items: [
    { label: 'Portal do Cliente', to: '/portal-cliente' }, { label: 'Portal do Motorista', to: '/portal-motorista' },
  ] },
];

export function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const filtered = COMMANDS.map(sec => ({
    ...sec, items: sec.items.filter(it => it.label.toLowerCase().includes(q.toLowerCase())),
  })).filter(sec => sec.items.length > 0);

  useEffect(() => { if (!open) setQ(''); }, [open]);

  const go = (to) => { navigate(to); onClose(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden">
        <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
          <Command className="h-4 w-4 text-neutral-500" />
          <input data-testid="command-palette-input" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Digite um comando ou pesquise..." className="flex-1 bg-transparent outline-none text-sm" />
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500">ESC</span>
        </div>
        <div className="max-h-96 overflow-y-auto p-2">
          {filtered.length === 0 ? <p className="p-4 text-sm text-neutral-500 text-center">Nada encontrado</p> :
            filtered.map(sec => (
              <div key={sec.section} className="mb-2">
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 px-2 py-1 font-semibold">{sec.section}</p>
                {sec.items.map((it, i) => (
                  <button key={i} data-testid={`cmd-item-${it.to}`} onClick={() => go(it.to)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-sm flex items-center justify-between">
                    <span>{it.label}</span>
                    <span className="text-[10px] text-neutral-400 font-mono">{it.to}</span>
                  </button>
                ))}
              </div>
            ))
          }
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NotificationPanel({ open, onClose }) {
  return (
    <MockDrawer open={open} onClose={onClose} title="Notificações">
      <div className="space-y-3">
        {mockNotifications.map(n => (
          <div key={n.id} className={`rounded-lg border p-3 ${n.read ? 'border-neutral-200 dark:border-neutral-800 opacity-70' : 'border-neutral-300 dark:border-neutral-700'}`}>
            <div className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.kind === 'critical' ? 'bg-red-500' : n.kind === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
              <div className="flex-1">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-neutral-400 mt-1">{n.time}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </MockDrawer>
  );
}

// Simple hook that opens command palette on Ctrl+K / Cmd+K
export function useCommandPalette(setOpen) {
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setOpen]);
}

// Generic mock page (dashboard + list + detail placeholder)
export function MockPageShell({ title, subtitle, breadcrumbs, kpis, charts, table, right, tabs, extra }) {
  return (
    <div>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <PageHeader title={title} subtitle={subtitle} action={right} />
      {tabs}
      {kpis && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">{kpis}</div>}
      {charts && <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">{charts}</div>}
      {extra}
      {table}
    </div>
  );
}

export const chartColors = COLORS;
